<template>
  <div class="chart-container">
    <h5 v-if="title" class="chart-title">{{ title }}</h5>
    <div v-if="loading" class="d-flex justify-content-center align-items-center h-100">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Chargement...</span>
      </div>
    </div>
    <div v-else-if="!hasData" class="d-flex justify-content-center align-items-center h-100">
      <div class="text-muted">Aucune donnée disponible</div>
    </div>
    <Bar
      v-else
      :chart-data="chartData"
      :chart-options="mergedOptions"
    />
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, LinearScale, CategoryScale } from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, LinearScale, CategoryScale)

export default {
  name: 'TopClientsBarChart',
  components: { Bar },
  props: {
    title: {
      type: String,
      default: 'Top Clients'
    },
    clients: {
      type: Array,
      default: () => []
    },
    valueKey: {
      type: String,
      default: 'montant'
    },
    labelKey: {
      type: String,
      default: 'nomClient'
    },
    loading: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number,
      default: 10
    },
    customOptions: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    hasData() {
      return this.clients && this.clients.length > 0;
    },
    sortedClients() {
      if (!this.hasData) return [];
      
      return [...this.clients]
        .sort((a, b) => b[this.valueKey] - a[this.valueKey])
        .slice(0, this.limit);
    },
    chartData() {
      const labels = this.sortedClients.map(client => client[this.labelKey]);
      const data = this.sortedClients.map(client => client[this.valueKey]);
      
      return {
        labels,
        datasets: [
          {
            label: 'Montant',
            data,
            backgroundColor: '#4e73df',
            borderColor: '#4e73df',
            borderWidth: 1
          }
        ]
      };
    },
    defaultOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed.x !== null) {
                  label += new Intl.NumberFormat('fr-FR', { 
                    style: 'currency', 
                    currency: 'EUR' 
                  }).format(context.parsed.x);
                }
                return label;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: {
              callback: (value) => {
                return new Intl.NumberFormat('fr-FR', { 
                  style: 'currency', 
                  currency: 'EUR',
                  maximumFractionDigits: 0
                }).format(value);
              }
            }
          },
          y: {
            ticks: {
              font: {
                size: 11
              }
            }
          }
        }
      };
    },
    mergedOptions() {
      return {
        ...this.defaultOptions,
        ...this.customOptions
      };
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 400px;
  width: 100%;
  padding: 15px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.chart-title {
  font-size: 1.1rem;
  margin-bottom: 15px;
}
</style>
